import { Injectable } from '@angular/core';
import { MultilingualService } from '@luggagehero/i18n';
import { LHTenant, SharedAppSettingsService } from '@luggagehero/shared/app-settings/data-access';
import { Config } from '@luggagehero/shared/environment';
import { AppInfo } from '@luggagehero/shared/interfaces';
import { SharedDocumentService } from '@luggagehero/shared/services/document';
import { SharedIntercomService } from '@luggagehero/shared/services/intercom';
import { SharedLoggingService } from '@luggagehero/shared/services/logging';
import { SharedParamsService } from '@luggagehero/shared/services/params';
import { SharedSplitTestingService } from '@luggagehero/shared/services/split-testing';
import { SharedStorageService } from '@luggagehero/shared/services/storage';
import { SharedThemeService } from '@luggagehero/shared/services/theme';
import { SharedUserService } from '@luggagehero/shared/services/users';
import { SharedWindowService, WindowPlatformService } from '@luggagehero/shared/services/window';
import { SharedUtilDate, SharedUtilGuid } from '@luggagehero/shared/util';
import { DateUtil } from '@luggagehero/utils';
import SuperTokens from 'supertokens-web-js';
import EmailPassword from 'supertokens-web-js/recipe/emailpassword';
import Multitenancy from 'supertokens-web-js/recipe/multitenancy';
import Session from 'supertokens-web-js/recipe/session';

import packageInfo from '../../../package.json';
import { DefaultAppSettings } from './app.settings';

/**
 * App initializer service which runs before the app is loaded
 */
@Injectable({
  providedIn: 'root',
})
export class AppInitializer {
  constructor(
    private userService: SharedUserService,
    private storageService: SharedStorageService,
    private paramsService: SharedParamsService,
    private appSettingsService: SharedAppSettingsService,
    private multilingualService: MultilingualService,
    private splitTestingService: SharedSplitTestingService,
    private intercomService: SharedIntercomService,
    private themeService: SharedThemeService,
    private documentService: SharedDocumentService,
    private windowService: SharedWindowService,
    private window: WindowPlatformService, // TODO: Can we call this via windowService?
    private log: SharedLoggingService,
  ) {}

  public async init(): Promise<void> {
    this.initSupertokens();

    let params: Record<string, string>;

    try {
      // Get query params from the url as an object
      params = Object.fromEntries(new URLSearchParams(this.window.location.search));
    } catch (err) {
      void this.log.error('Error parsing query params', err);
      params = {};
    }

    //
    // Create session and visitor ids and store them locally before making the first api calls as they will be added to
    // http request headers
    //
    await this.initSession(params); // TODO: Do we need to await this here?

    //
    // Wait for window service before doing other initialization as other services depend on the window service during
    // their initialization (note that the native window service also waits for the Ionic platform to be ready)
    //
    const app: AppInfo = { name: packageInfo.name, version: packageInfo.version };
    await this.windowService.init(app);

    // Initialize app settings which are required for other initialization tasks
    await this.appSettingsService.init(app.name, this.windowService.platform, DefaultAppSettings);

    // Parse tracking data from query params
    this.paramsService.parseTrackingData(params);

    // Check if the app update is required (this may refresh the page/reload the app)
    this.windowService.checkAppVersion(this.appSettingsService.current.minAppVersion);

    //
    // Start intialization of other services which should be ready before the app is loaded, but which no other
    // intialization logic depends upon, so they can run in parallel to speed up the app load time (e.g. in case of
    // network requests)
    //
    const asyncTasks = [
      this.multilingualService.init(params).catch((err) => void this.log.error(`Error initializing language`, err)),
      this.themeService.init().catch((err) => void this.log.error(`Error initializing theme`, err)),
    ];

    this.initSplitTesting(params);

    // Initialize Intercom without waiting for it to be ready
    void this.intercomService.init(app.name).catch((err) => void this.log.error(`Error initializing Intercom`, err));

    // Set body classes required for styling
    this.documentService.addBodyClass(this.windowService.isTouch ? 'touchevents' : 'no-touchevents');

    try {
      // Configure utils which cannot depend on app settings service directly
      DateUtil.TimeFormatDetectionVersion = this.appSettingsService.current.TIME_FORMAT_DETECTION_VERSION;
      SharedUtilDate.TimeFormatDetectionVersion = this.appSettingsService.current.TIME_FORMAT_DETECTION_VERSION;
    } catch (err) {
      void this.log.error('Error configuring utils', err);
    }

    // Wait for all async tasks to complete
    await Promise.all(asyncTasks);
  }

  private initSupertokens() {
    SuperTokens.init({
      appInfo: {
        apiDomain: Config.environment.IDENTITY_ST_API,
        appName: 'LuggageHero Storage Manager',
      },
      recipeList: [
        Session.init({
          sessionTokenFrontendDomain: Config.isDevelopment
            ? 'localhost'
            : Config.isStaging
              ? '.devheroo.com'
              : Config.isProduction
                ? '.luggagehero.com'
                : '.devheroo.com',
          tokenTransferMethod: 'cookie',
          override: {
            functions: (originalImplementation) => ({
              ...originalImplementation,
              shouldDoInterceptionBasedOnUrl: (toCheckUrl, apiDomain, sessionTokenBackendDomain) => {
                if (Config.environment.ENV === 'DEV' && toCheckUrl.includes('localhost')) {
                  return true;
                }

                // FIX: if we are logged in on a dev/staging env we should not intercept requests to the main site
                if (toCheckUrl.includes('.devheroo.com') || toCheckUrl.includes('.luggagehero.com')) {
                  return true;
                }

                // Call the original implementation for all other cases
                return originalImplementation.shouldDoInterceptionBasedOnUrl(
                  toCheckUrl,
                  apiDomain,
                  sessionTokenBackendDomain,
                );
              },
            }),
          },
        }),
        EmailPassword.init(),
        Multitenancy.init({
          override: {
            functions: (originalImplementation) => {
              return {
                ...originalImplementation,
                getTenantId: () => {
                  return LHTenant;
                },
              };
            },
          },
        }),
      ],
    });
  }

  private initSplitTesting(params: Record<string, string>): void {
    try {
      // Enable experiment and variant if provided
      this.splitTestingService.init(params);

      // Apply variant-specific configurations
      switch (this.splitTestingService.variant) {
        // case this.appSettingsService.current.EXPERIMENT_VARIANTS.someVariant: {
        //   this.appSettingsService.current.SOME_CONFIG_PROPERTY = true;
        //   break;
        // }
        case this.appSettingsService.current.EXPERIMENT_VARIANTS.original:
        default: {
          break;
        }
      }
    } catch (err) {
      void this.log.error('Error initializing split testing', err);
    }
  }

  private async initSession(params: Record<string, string>): Promise<void> {
    try {
      //
      // Create session and visitor ids and store them locally before making the first api calls as they will be added to
      // http request headers. Take visitor id from query params first if provided (e.g. when passed from landing site);
      // otherwise, take from storage if present there, or ultimately create a new one.
      //
      this.storageService.visitorId = params.visitorId || this.storageService.visitorId || SharedUtilGuid.create();
      this.storageService.sessionId = SharedUtilGuid.create();

      // force a logout if session no longer present
      const isLoggedIn = await this.userService.isLoggedInAsync();

      if (!isLoggedIn) {
        this.userService.softLogout();
      }
    } catch (err) {
      void this.log.error('Error initializing session', err);
    }
  }
}
